import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { setAccessToken, setRefreshToken, getRefreshToken, clearTokens, getAccessToken } from '../services/api';
import { mapBackendUserToFrontend } from '../utils/dataMappers';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://rahmani30-collaborate-api.rahmani30reactapp.workers.dev/api';



interface User {
  id: number;
  userCode?: string;
  name: string;
  fullName?: string;
  email: string;
  phoneNumber?: string;
  
  phone?: string;
  role: string;
  postalCode?: string;
  state?: string;
  district?: string;
  city?: string;
  blockTehsil?: string;
  panCardNo?: string;
  image?: string;
  address?: string;
  pincode?: string;
  isEmailVerified?: boolean;
  isBlocked?: boolean;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;

  isAmbassador?: boolean;
  ambassadorStatus?: 'PENDING' | 'APPROVED' | 'REJECTED' | null;
  ambassadorAppliedAt?: string;
  ambassadorApprovedAt?: string;
  ambassadorId?: string;
  ambassadorRecord?: any;

  donorType?: string;
  
  donor_type?: string;
  kycStatus?: string;
  kycSubmittedAt?: string;

  ambassadorName?: string;
  ambassadorPhone?: string;
  whatsappNumber?: string;
  fullAddress?: string;
  country?: string;
  isWhatsappSameAsMobile?: boolean | number;
  referencerId?: string;

  photoIdType?: string;
  photoIdNumber?: string;
  passportSizePhoto?: string;
  idDocumentScan?: string;
  idDocumentScanBack?: string;
  addressProofType?: string;
  addressProofNumber?: string;
  addressDocumentScan?: string;
  addressDocumentScanBack?: string;
  addressProofSameAsPhotoId?: boolean | number;
  alumniVerified?: boolean | number;

  orgName?: string;
  orgEmail?: string;
  orgPan?: string;
  orgGovtId?: string;
  orgRepName?: string;
  orgRepEmail?: string;
  orgRepPhone?: string;
  orgRepWhatsapp?: string;
  isOrgWhatsappSameAsMobile?: boolean | number;
  orgPanScan?: string;
  orgGovtIdScan?: string;
  declarationAgreed?: boolean | number;
  isReferencer?: boolean;
  referencerRecord?: any;

  consentForProfileSharing?: boolean | number;
  consentGivenAt?: string | null;
}


interface AuthContextType {
  user: User | null;
  token: string | null;
  logout: () => void;
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isAmbassador: boolean;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const logout = useCallback(async () => {
    try {
      const rfToken = getRefreshToken();
      await axios.post(`${API_BASE_URL}/auth/logout`, { refreshToken: rfToken }, {
        headers: { Authorization: `Bearer ${getAccessToken()}` },
      });
    } catch (_) { }
    setUser(null);
    setToken(null);
    clearTokens();
    window.location.href = '/login';
  }, []);

  const fetchFullUserProfile = useCallback(async (accessToken: string, currentUser: User) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/users/profile`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const userData = response.data?.user || response.data;
      if (userData) {
        const fullUser = mapBackendUserToFrontend(userData, currentUser);
        setUser(fullUser);
        try {
          localStorage.setItem('user', JSON.stringify({ id: fullUser.id, userCode: fullUser.userCode, name: fullUser.name, email: fullUser.email, role: fullUser.role }));
        } catch (_) { }
      }
    } catch (_) {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const handler = () => {
      setUser(null);
      setToken(null);
      clearTokens();
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    };

    const expiredHandler = () => {
      window.dispatchEvent(new CustomEvent('toast:error', { detail: 'Your session has expired. Please sign in again.' }));
    };

    const suspendedHandler = () => {
      setUser(null);
      setToken(null);
      if (window.location.pathname !== '/login') {
        window.location.href = '/login?suspended=1';
      }
    };

    window.addEventListener('auth:unauthorized', handler);
    window.addEventListener('auth:session-expired', expiredHandler);
    window.addEventListener('auth:suspended', suspendedHandler);
    return () => {
      window.removeEventListener('auth:unauthorized', handler);
      window.removeEventListener('auth:session-expired', expiredHandler);
      window.removeEventListener('auth:suspended', suspendedHandler);
    };
  }, []);

  const initAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const currentPath = window.location.pathname;
    const isOAuthCallback = ['/oauth2/callback', '/callback'].includes(currentPath);

    if (isOAuthCallback) {
      setLoading(false);
      return;
    }

    const savedUser = localStorage.getItem('user');
    const savedRefreshToken = getRefreshToken();

    if (!savedUser || !savedRefreshToken) {
      setLoading(false);
      return;
    }

    initAbortRef.current?.abort();
    const abortCtrl = new AbortController();
    initAbortRef.current = abortCtrl;

    let parsedUser: User;
    try {
      parsedUser = JSON.parse(savedUser);
    } catch {
      clearTokens();
      setLoading(false);
      return;
    }

    setUser(parsedUser);

    axios.post(
      `${API_BASE_URL}/auth/refresh`,
      { refreshToken: savedRefreshToken },
      { signal: abortCtrl.signal }
    )
      .then((res) => {
        if (abortCtrl.signal.aborted) return;
        const { token: newToken, refreshToken: newRefreshToken } = res.data;
        setAccessToken(newToken);
        setRefreshToken(newRefreshToken);
        setToken(newToken);
        fetchFullUserProfile(newToken, parsedUser);
      })
      .catch((err) => {
        if (axios.isCancel(err) || abortCtrl.signal.aborted) return;
        clearTokens();
        setUser(null);
        setToken(null);
        setLoading(false);
        if (currentPath !== '/login') {
          window.location.href = '/login';
        }
      });

    return () => {
      abortCtrl.abort();
    };
  }, [fetchFullUserProfile]);


  const value = useMemo(() => ({
    user,
    token,
    logout,
    setUser,
    setToken,



    isAuthenticated: !!token || (loading && !!user),
    isAdmin: user?.role === 'ADMIN',
    isAmbassador: user?.role === 'AMBASSADOR' || user?.ambassadorStatus === 'APPROVED' || user?.isAmbassador === true,
    loading,

  }), [user, token, loading, logout, setUser, setToken]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
